import { Button } from '@vrplatform/components';
import { isGoogleTranslateActive } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { Loader } from '@mantine/core';
import { Avatar, Card, Center, Text, Title } from '@mantine/core';
import {
  ErrorBoundary as Boundary,
  type ErrorBoundaryProps,
  type FallbackRender,
} from '@sentry/react';

type Props = Pick<Parameters<FallbackRender>[0], 'error'>;

export const ErrorFallback = ({ error: initial }: Props) => {
  const chunkFailedMessage = /Failed to fetch dynamically imported module/;
  const mimeTypeMessage = /is not a valid JavaScript MIME type/;
  const domMutationMessage =
    /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/;

  const error = initial as Error | undefined;

  const isChunkError =
    error?.message &&
    (chunkFailedMessage.test(error.message) ||
      mimeTypeMessage.test(error.message));
  const isDomMutationError =
    error?.message && domMutationMessage.test(error.message);

  if (isChunkError) {
    return (
      <Center sx={{ height: '100%', flex: 0.8, flexDirection: 'column' }}>
        <Loader type="dots" c="gray" />
        <Text mb="xs" mt="sm">
          Loading latest version...
        </Text>
        <Text size="xs" c="gray" mb="md">
          This can take a minute. Please refresh the page and contact support if
          the issue persists.
        </Text>
        <Button
          size="xs"
          variant="transparent"
          onClick={() => window.location.reload()}
          leftIcon="RefreshCwIcon"
        >
          Refresh
        </Button>
      </Center>
    );

    // return (
    //   <Box
    //     sx={{
    //       position: 'fixed',
    //       display: 'flex',
    //       alignItems: 'center',
    //       justifyContent: 'center',
    //       padding: 0,
    //       margin: 0,
    //       inset: 0,
    //       zIndex: 10000,
    //       backgroundColor: 'rgba(0, 0, 0, 0.5)',
    //       overflow: 'hidden',
    //     }}
    //   >
    //     <Card withBorder maw={400} p={rem(32)} radius="md">
    //       <Icon
    //         icon="RefreshCwIcon"
    //         size={30}
    //         color={(theme) => theme.colors[theme.primaryColor][5]}
    //         sx={(theme) => ({
    //           display: 'block',
    //           marginInline: 'auto',
    //           marginTop: theme.spacing.md,
    //           marginBottom: theme.spacing.xl,
    //         })}
    //       />
    //       <Title order={3} c="dark" ta="center" mb="md">
    //         Update Available
    //       </Title>
    //       <Text component="p" ta="center" mb="xl" c="neutral">
    //         New content available. Update now to use the latest version of{' '}
    //         {appName}.{' '}
    //       </Text>
    //       <Button
    //         onClick={() => window.location.reload()}
    //         sx={{
    //           width: '100%',
    //         }}
    //       >
    //         Reload
    //       </Button>
    //     </Card>
    //   </Box>
    // );
  }

  if (isDomMutationError) {
    const isGoogleTranslateDetected = isGoogleTranslateActive();

    return (
      <Center sx={{ height: '100%', flex: 0.8 }}>
        <Card
          role="alert"
          sx={() => ({
            width: '100%',
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          })}
        >
          <Avatar color="yellow" variant="filled" mt="md" size={35} radius={35}>
            <Icon icon="AlertTriangleIcon" color={'#fff'} size={22} />
          </Avatar>
          <Title order={3} c="dark" ta="center" fw={500} mb="md" mt="lg">
            {isGoogleTranslateDetected
              ? 'Translation tools can break this page'
              : 'Oops! Something went wrong!'}
          </Title>

          <Text mb="lg" ta="center" c="neutral" lh="1.6">
            {isGoogleTranslateDetected
              ? 'A translation tool may have modified the page and caused an error. Please disable translation for this site (for example, Google Translate) and refresh the page.'
              : 'Something went wrong! Please try to reload the page and if the problem persists, contact our support.'}
          </Text>
          <Button
            size="sm"
            variant="default"
            onClick={() => window.location.reload()}
            leftIcon="RefreshCwIcon"
          >
            Refresh
          </Button>
        </Card>
      </Center>
    );
  }

  return (
    <Center sx={{ height: '100%', flex: 0.8 }}>
      <Card
        role="alert"
        sx={() => ({
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        <Avatar color="yellow" variant="filled" mt="md" size={35} radius={35}>
          <Icon icon="AlertTriangleIcon" color={'#fff'} size={24} />
        </Avatar>
        <Title order={3} c="dark" ta="center" fw={500} mb="md" mt="lg">
          Ooops, something went wrong!
          {/* {error?.message || 'Missing error message.'} */}
        </Title>

        <Text mb="lg" ta="center" c="neutral" lh="1.6">
          Something went wrong! Please try to reload the page and if the problem
          persists, contact our support.
        </Text>
        <Button
          onClick={() => window.location.reload()}
          sx={{
            width: '100%',
            maxWidth: '200px',
          }}
        >
          Try again
        </Button>
      </Card>
    </Center>
  );
};

export function ErrorBoundary({
  children,
  onReset,
  ...props
}: ErrorBoundaryProps) {
  return (
    <Boundary fallback={ErrorFallback} onReset={onReset} {...props}>
      {children}
    </Boundary>
  );
}
